// LIVEMOA-LIVE-TEST-PREVIEW-INTEGRATION-1 · 테스트 소비자 보기 / 외부 송출 미리보기.
//   실제 LiveKitViewer · EgressTemplate 페이지를 same-origin iframe 으로 그대로 띄우고
//   Studio 의 현재 liveCtx 를 postMessage 로 전달 (API·LiveKit·주문·채팅 DB 미호출).
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft } from 'lucide-react';

const CANVAS = { portrait: { w: 1080, h: 1920 }, landscape: { w: 1280, h: 720 } };

const useFitScale = (ref, w, h) => {
  const [s, setS] = useState(0.2);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const upd = () => setS(Math.min(el.clientWidth / w, el.clientHeight / h));
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    upd();
    return () => ro.disconnect();
  }, [ref, w, h]);
  return s;
};

const PreviewFrame = ({ src, ctx, style, testid }) => {
  const ref = useRef(null);
  useEffect(() => {
    const post = () => ref.current?.contentWindow?.postMessage({ type: 'livemoa-preview-ctx', ctx }, window.location.origin);
    post();
    const on = (e) => {
      if (e.origin === window.location.origin && e.source === ref.current?.contentWindow && e.data?.type === 'livemoa-preview-ready') post();
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, [ctx]);
  return <iframe ref={ref} src={src} title={testid} className="border-0" style={style} data-testid={testid} />;
};

const EgressBody = ({ ctx }) => {
  const [orient, setOrient] = useState('portrait');
  const box = useRef(null);
  const { w, h } = CANVAS[orient];
  const s = useFitScale(box, w, h);
  return (
    <>
      <div className="shrink-0 flex justify-center gap-2 py-2" data-testid="live-preview-egress-orient">
        {['portrait', 'landscape'].map((o) => (
          <button key={o} type="button" onClick={() => setOrient(o)}
                  className={`px-3 py-1 rounded-full text-[12px] font-black border ${orient === o ? 'bg-white text-black border-white' : 'text-white border-white/30'}`}
                  data-testid={`live-preview-egress-${o}`}>
            {o === 'portrait' ? '세로 9:16' : '가로 16:9'}
          </button>
        ))}
      </div>
      <div className="flex-1 min-h-0 px-3 pb-3">
      <div ref={box} className="w-full h-full flex items-center justify-center overflow-hidden">
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-b from-[#3a4a5c] to-[#6b5a4c]"
             style={{ width: w * s, height: h * s }} data-testid="live-preview-egress-canvas">
          <span className="absolute inset-0 flex items-center justify-center text-white/40 text-[11px] font-bold">카메라 영상 자리</span>
          <PreviewFrame key={orient} ctx={ctx} testid="live-preview-egress-frame"
                        src={`/egress-template/__TEST_SESSION__?orientation=${orient}&preview=1&source=parent`}
                        style={{ position: 'absolute', top: 0, left: 0, width: w, height: h, transform: `scale(${s})`, transformOrigin: 'top left', background: 'transparent' }} />
        </div>
      </div>
      </div>
      <p className="shrink-0 text-center text-[10px] text-white/50 pb-[max(env(safe-area-inset-bottom),0.5rem)]">
        실제 송출 Renderer 화면 · {w}×{h} · 하단 점선은 미리보기 전용 안전영역 · 실제 Egress 합성은 미검증
      </p>
    </>
  );
};

// PC-CONSOLE-OVERLAY-PRESET-CONSUMER-VIEW-1 · body 포털(부모 sticky stacking 탈출) · Esc 닫기 · 돌아가기 문구 지정.
export const LivePreviewModal = ({ kind, ctx, onClose, backLabel = 'Studio로 돌아가기' }) => {
  useEffect(() => {
    if (!kind) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [kind, onClose]);
  if (!kind) return null;
  return createPortal(
    <div className="fixed inset-0 z-[300] bg-black text-white flex flex-col overflow-hidden" style={{ height: '100dvh' }} data-testid={`live-preview-modal-${kind}`}>
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-white/10" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}>
        <button type="button" onClick={onClose} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 text-[12px] font-black"
                data-testid="live-preview-back">
          <ArrowLeft size={14} /> {backLabel}
        </button>
        <span className="text-[12px] font-black truncate">{kind === 'viewer' ? '소비자 보기' : '외부 송출 미리보기'}</span>
        <span className="ml-auto text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-400 text-black">TEST · 송출 없음</span>
      </div>
      {kind === 'viewer' ? (
        <div className="flex-1 min-h-0 relative">
          <PreviewFrame ctx={ctx} testid="live-preview-viewer-frame" src="/live-view/__TEST_SESSION__?source=parent"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        </div>
      ) : <EgressBody ctx={ctx} />}
    </div>,
    document.body,
  );
};

export default LivePreviewModal;
